import { store } from '../../store';
import { buildLayoutBom, resolveLayoutBomMetersPerWorldUnit } from '../../utils/bom';
import { buildAssistantLayoutSummary, type AssistantLayoutSummary } from './assistantLayoutSummary';

/**
 * Read-only snapshot of the layout, its validation state and the latest
 * simulation KPIs, taken from the store at send time so the chat widget does
 * not re-render on every live-stream batch. The assistant answers factual
 * questions from it.
 *
 * Throws nothing: a failure here must never block the message, so the caller
 * gets `null` and the error is reported to the console for diagnosis.
 */
export function snapshotLayoutSummary(): AssistantLayoutSummary | null {
  try {
    const state = store.getState();
    const bom = buildLayoutBom({
      elements: state.layout.elements,
      toolboxItems: state.toolbox.items,
      calibration: {
        floorplanBounds: Boolean(state.floorplan.bounds),
        floorplanPolylineCount: state.floorplan.polylines.length,
        scaleFactor: state.floorplan.scaleFactor,
        dxfUnitsPerMeter: state.floorplan.dxfUnitsPerMeter,
        millimetersPerCanvasUnit: state.floorplan.millimetersPerCanvasUnit,
        canvasUnitsPerMeter: state.floorplan.canvasUnitsPerMeter,
      },
    });
    const sim = state.simulation;
    const fp = state.floorplan;
    const metersPerWorldUnit = resolveLayoutBomMetersPerWorldUnit({
      floorplanBounds: Boolean(fp.bounds),
      floorplanPolylineCount: fp.polylines.length,
      scaleFactor: fp.scaleFactor,
      dxfUnitsPerMeter: fp.dxfUnitsPerMeter,
      millimetersPerCanvasUnit: fp.millimetersPerCanvasUnit,
      canvasUnitsPerMeter: fp.canvasUnitsPerMeter,
    });
    return buildAssistantLayoutSummary({
      bom,
      validation: state.validation,
      floorPlan: {
        name: fp.name,
        floorPlanId: fp.floorPlanId,
        bounds: fp.bounds,
        dxfUnit: fp.dxfUnit,
        dxfUnitsPerMeter: fp.dxfUnitsPerMeter,
        metersPerWorldUnit,
        polylineCount: fp.polylines.length,
        labelCount: fp.labels.length,
        layers: fp.layers,
        visible: fp.visible,
        regions: state.facility.regions,
      },
      equipment: { instances: Object.values(state.equipment.instances) },
      simulation: {
        source: sim.source,
        running: sim.running,
        simulationTime: sim.simulationTime,
        live: {
          connectionStatus: sim.live.connectionStatus,
          dispatchPolicy: sim.live.dispatchPolicy,
          simSpeed: sim.live.simSpeed,
          liveReport: sim.live.liveReport,
        },
        lastLiveReport: sim.lastLiveReport,
      },
    });
  } catch (error) {
    console.warn('[assistant] layout summary unavailable for this message', error);
    return null;
  }
}
